/* eslint-disable @typescript-eslint/no-misused-promises */
import { Trans, t } from '@lingui/macro'
import React, { useState } from 'react'
import { type SubmitHandler, useForm, Controller } from 'react-hook-form'
import { fetchChangePassword } from '../../redux/thunks/userThunks'
import { useDispatch } from 'react-redux'
import { type AppDispatch } from '../../redux/store'
import Loader from '../global/Loader'
import ModalWindow from '../global/ModalWindow'
import { type ModalWindowData } from '../../types'
import FadeAnimation from '../global/FadeAnimation'
import IconSuccess from '../icons/IconSuccess'
import IconError from '../icons/IconError'
import { useNavigate } from 'react-router-dom'

interface ChangePassword {
    password: string
    newPassword: string
    confirmNewPassword: string
}

const FormChangePassword: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)
    const [textModal, setTextModal] = useState<ModalWindowData>({ title: '', subtitle: '', textButton: '' })
    const navigate = useNavigate()

    const closeWindow = (): void => {
        setOpen(false)
    }
    const {
        register,
        handleSubmit,
        control,
        getValues,
        formState: { errors }
    } = useForm<ChangePassword>({ })

    const onSubmit: SubmitHandler<ChangePassword> = (data) => {
        const formData = new FormData()

        formData.append('password', data.password)
        formData.append('new_password', data.newPassword)
        formData.append('confirm_password', data.confirmNewPassword)
        setLoading(true)
        void dispatch(fetchChangePassword(formData))
            .then((response: any) => {
                setLoading(false)
                if (response.payload.current_password !== undefined) {
                    setTextModal({
                        title: t`Nesprávne heslo`,
                        subtitle: t`Zadali ste nesprávne heslo. Skúste znova.`,
                        textButton: t`Zatvoriť`,
                        icon: <IconError/>,
                        action: () => { navigate('/profil') }
                    })
                    setOpen(true)
                } else {
                    setTextModal({
                        title: t`Heslo zmenené`,
                        subtitle: t`Vaše heslo bolo úspešne zmenené.`,
                        textButton: t`Zatvoriť`,
                        icon: <IconSuccess/>,
                        action: () => { navigate('/profil') }
                    })
                    setOpen(true)
                }
            })
    }

    return (
        <>
            <ModalWindow
                open={open}
                setOpen={setOpen}
                dataModalWindow={textModal}
                closeWindow={closeWindow}
            />
            <div className='absolute top-[265px] left-1/2 '>
                <Loader loading={loading}/>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="md:w-36 pb-3 w-full mx-auto ">
                    <label htmlFor="name" className="sm-font-b ">
                        <Trans>Vaše heslo*</Trans>
                    </label>
                    <input
                        {...register('password', {
                            required: {
                                value: true,
                                message: t`Pole je povinné`
                            }
                        })}
                        name="password"
                        placeholder='*******'
                        type='password'
                        className='input-form mt-[6px] input-focus input-transition input-text-l input-placeholder'
                        autoComplete="off"
                    />
                    {(errors.password != null) && (
                        <FadeAnimation type="up" key={errors.password.message}>
                            <div className="error-text">
                                {errors.password.message}
                            </div>
                        </FadeAnimation>
                    )}
                </div>
                <div className="md:w-36 pb-3 w-full mx-auto ">
                    <label htmlFor="name" className="sm-font-b">
                        <Trans>Nové heslo*</Trans>
                    </label>
                    <Controller
                        name="newPassword"
                        control={control}
                        defaultValue=""
                        rules={{
                            required: t`Pole je povinné`,
                            minLength: {
                                value: 8,
                                message: t`Heslo musí obsahovať aspoň 8 znakov`
                            },
                            validate: (value) => {
                                const hasUppercase = /[A-Z]/.test(value)
                                const hasLowercase = /[a-z]/.test(value)
                                const hasNumber = /\d/.test(value)
                                if (!hasUppercase) {
                                    return t`Heslo musí obsahovať aspoň jedno veľké písmeno`
                                }
                                if (!hasLowercase) {
                                    return t`Heslo musí obsahovať aspoň jedno malé písmeno`
                                }
                                if (!hasNumber) {
                                    return t`Heslo musí obsahovať aspoň jedno číslo`
                                }
                                return true
                            }
                        }}
                        render={({ field }) =>
                            <input
                                className="input-form py-[5px] mt-[6px] input-focus input-transition input-text-l input-placeholder"
                                type="password" {...field}
                                placeholder='*******'
                                autoComplete="off"
                            />
                        }
                    />
                    {(errors.newPassword != null) && (
                        <FadeAnimation type="up" key={errors.newPassword.message}>
                            <div className="error-text">
                                {errors.newPassword.message}
                            </div>
                        </FadeAnimation>
                    )}
                </div>
                <div className="md:w-36 w-full mx-auto ">
                    <label htmlFor="name" className="sm-font-b">
                        <Trans>Zopakujte nové heslo*</Trans>
                    </label>
                    <Controller
                        name="confirmNewPassword"
                        control={control}
                        defaultValue=""
                        rules={{
                            required: t`Pole je povinné`,
                            minLength: {
                                value: 8,
                                message: t`Heslo musí obsahovať aspoň 8 znakov`
                            },
                            validate: {
                                matchesNewPassword: (value) => {
                                    const newPassword = getValues('newPassword')
                                    return newPassword === value || t`Heslá sa nezhodujú`
                                },
                                hasUppercase: (value) => /[A-Z]/.test(value) || t`Heslo musí obsahovať aspoň jedno veľké písmeno`,
                                hasLowercase: (value) => /[a-z]/.test(value) || t`Heslo musí obsahovať aspoň jedno malé písmeno`,
                                hasNumber: (value) => /\d/.test(value) || t`Heslo musí obsahovať aspoň jedno číslo`
                            }
                        }}
                        render={({ field }) =>
                            <input
                                className="input-form py-[5px] mt-[6px] input-focus input-transition input-text-l input-placeholder"
                                type="password" {...field}
                                placeholder='*******'
                                autoComplete="off"
                            />
                        }
                    />
                    {(errors.confirmNewPassword != null) && (
                        <FadeAnimation type="up" key={errors.confirmNewPassword.message}>
                            <div className="error-text">
                                {errors.confirmNewPassword.message}
                            </div>
                        </FadeAnimation>
                    )}
                </div>
                <button
                    className='btn btn-red btn-m  block mx-auto mt-8'
                    type="submit"
                >
                    <Trans>Zmeniť heslo</Trans>
                </button>
            </form>
        </>
    )
}

export default FormChangePassword
